import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { cn } from '../../lib/utils'
import { getTableRowNumber } from '../../../helpers/tableRowNumber'
export type TableSortDirection = 'asc' | 'desc'

export type TableSequencePagination = {
 page: number
 perPage: number
 /** Total items in the current filtered list (for desc # numbering) */
 total?: number
}

export const TableSequencePaginationContext =
 React.createContext<TableSequencePagination | null>(null)

export const TableOptionsContext = React.createContext<{
 sequenced: boolean
 compact: boolean
}>({ sequenced: false, compact: false })

export type TableSequenceSortContextValue = {
 activeKey: string | null
 direction: TableSortDirection
 onSort: (key: string) => void
 /** API sort_by key for the # column (default: id) */
 sortKey?: string
} | null

export const TableSequenceSortContext =
 React.createContext<TableSequenceSortContextValue>(null)

export function TableSequencePaginationProvider({
 page,
 perPage,
 total,
 children,
 sortBy,
 sortDirection,
 onSort,
 sequenceSortKey = 'id',
}: TableSequencePagination & {
 children: React.ReactNode
 sortBy?: string | null
 sortDirection?: TableSortDirection
 onSort?: (key: string) => void
 sequenceSortKey?: string
}) {
 const value = React.useMemo(
 () => ({
 page: Math.max(1, page),
 perPage: Math.max(1, perPage),
 total: total != null ? Math.max(0, total) : undefined,
 }),
 [page, perPage, total],
 )

 const sortValue = React.useMemo((): TableSequenceSortContextValue => {
 if (!onSort) return null
 return {
 activeKey: sortBy ?? null,
 direction: sortDirection ?? 'desc',
 onSort,
 sortKey: sequenceSortKey,
 }
 }, [sortBy, sortDirection, onSort, sequenceSortKey])

 return (
 <TableSequencePaginationContext.Provider value={value}>
 <TableSequenceSortContext.Provider value={sortValue}>
 {children}
 </TableSequenceSortContext.Provider>
 </TableSequencePaginationContext.Provider>
 )
}

export { getTableRowNumber }

const SEQUENCE_MARK = 'data-table-sequence'

const sequenceHeadClass =
 'erp-table-seq-head w-11 min-w-[2.75rem] max-w-[2.75rem] px-2 text-center text-[11px] font-semibold text-muted-foreground'

const sequenceCellClass =
 'erp-table-seq-cell w-11 min-w-[2.75rem] max-w-[2.75rem] px-2 text-center text-xs tabular-nums font-medium text-muted-foreground'

export function TableSequenceHead() {
  const { t } = useTranslation()
  const sort = React.useContext(TableSequenceSortContext)
  const sortKey = sort?.sortKey ?? 'id'

  if (sort) {
    const isActive = sort.activeKey === sortKey
    const SortIcon = isActive
      ? sort.direction === 'asc'
        ? ArrowUp
        : ArrowDown
      : ArrowUpDown

    return (
      <th
        {...{ [SEQUENCE_MARK]: 'head' }}
        className={cn(sequenceHeadClass, 'group/th cursor-pointer select-none p-0')}
        scope="col"
        aria-sort={isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        aria-label={t('ui.sortBySequence')}
        onClick={(e) => {
          e.stopPropagation()
          sort.onSort(sortKey)
        }}
      >
        <span
          className={cn(
            'flex h-12 w-full items-center justify-center gap-1 transition-colors duration-150',
            'group-hover/th:bg-muted/70',
            isActive && 'bg-muted text-foreground',
          )}
        >
          <span className="text-[10px] font-extrabold tracking-widest">#</span>
          <SortIcon
            className={cn(
              'h-3.5 w-3.5 shrink-0',
              isActive
                ? 'text-[hsl(var(--brand-accent))]'
                : 'text-muted-foreground/30 group-hover/th:text-muted-foreground/70',
            )}
            strokeWidth={isActive ? 2.5 : 2}
            aria-hidden
          />
        </span>
      </th>
    )
  }

  return (
    <th
      {...{ [SEQUENCE_MARK]: 'head' }}
      className={sequenceHeadClass}
      scope="col"
      aria-label={t('ui.sequenceColumn')}
    >
      <span className="text-[10px] font-extrabold tracking-widest">#</span>
    </th>
  )
}

export function TableSequenceCell({ index }: { index: number }) {
  const pagination = React.useContext(TableSequencePaginationContext)
  const sort = React.useContext(TableSequenceSortContext)
  const sequenceSortKey = sort?.sortKey ?? 'id'
  const sequenceSortActive = sort?.activeKey === sequenceSortKey

  const n = pagination
    ? getTableRowNumber(pagination.page, pagination.perPage, index, {
        total: pagination.total,
        sequenceSortActive,
        sortDirection: sort?.direction,
      })
    : index + 1

  return (
    <td {...{ [SEQUENCE_MARK]: 'cell' }} className={sequenceCellClass}>
      <span className="erp-table-seq-badge tabular-nums">{n}</span>
    </td>
  )
}

function rowHasSequenceColumn(children: React.ReactNode): boolean {
 return React.Children.toArray(children).some(
 (child) =>
 React.isValidElement(child) &&
 typeof child.props === 'object' &&
 child.props !== null &&
 SEQUENCE_MARK in (child.props as Record<string, unknown>),
 )
}

function isTableRowElement(child: React.ReactNode): child is React.ReactElement {
 if (!React.isValidElement(child)) return false
 const type = child.type as React.ElementType & { displayName?: string }
 return type === TableRowRef || type?.displayName === 'TableRow'
}

/** @internal set by table.tsx after TableRow is defined */
export let TableRowRef: React.ElementType = 'tr'

export function setTableRowRef(type: React.ElementType) {
 TableRowRef = type
}

export function injectSequenceIntoHeaderRows(children: React.ReactNode): React.ReactNode {
 return React.Children.map(children, (child) => {
 if (!isTableRowElement(child)) return child
 if (rowHasSequenceColumn(child.props.children)) return child

 return React.cloneElement(child, {}, [
 <TableSequenceHead key="__table-seq-head" />,
 ...React.Children.toArray(child.props.children),
 ])
 })
}

export function injectSequenceIntoBodyRows(children: React.ReactNode): React.ReactNode {
 let dataRowIndex = 0

 return React.Children.map(children, (child) => {
 if (!isTableRowElement(child)) return child
 if (rowHasSequenceColumn(child.props.children)) return child

 const cells = React.Children.toArray(child.props.children)
 const firstCell = cells[0]
 const colSpan =
 React.isValidElement(firstCell) &&
 typeof firstCell.props === 'object' &&
 firstCell.props !== null
 ? Number((firstCell.props as { colSpan?: number }).colSpan ?? 0)
 : 0

 if (colSpan > 1) {
 const adjustedFirst = React.cloneElement(firstCell as React.ReactElement, {
 colSpan: colSpan + 1,
 })

 return React.cloneElement(child, {}, [adjustedFirst, ...cells.slice(1)])
 }

 const seqCell = <TableSequenceCell key={`__table-seq-${dataRowIndex}`} index={dataRowIndex} />
 dataRowIndex += 1

 return React.cloneElement(child, {}, [seqCell, ...cells])
 })
}

export function useTableSequencingEnabled(): boolean {
 const options = React.useContext(TableOptionsContext)
 return options.sequenced && !options.compact
}
